import { format, parse } from 'date-fns';
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { editLead, getLead, getLeadServices, getLocations, updateLeadServices } from "../services/api";
import { formatPhoneNumber, LEAD_CATEGORIES, LEAD_LOSS_REASONS, NOT_APPLICABLE_REASONS, LEAD_SOURCES, LEAD_STATUSES } from '../utils/displayFormatters';
import ServiceSelector from './ServiceSelector';

const LeadEdit = ({ authToken }) => {
  const navigate = useNavigate();
  const { leadId } = useParams();

  console.log("Lead ID from params:", leadId);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    status: "",
    lead_category: "prospect",
    source: "",
    follow_up_date: null,
    loss_reason: "",
    loss_reason_notes: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
    },
  });
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(() => {
    return localStorage.getItem("hasInteractedWithFollowUpDate") === "true";
  });
  const [followUpDismissed, setFollowUpDismissed] = useState(() => {
    return localStorage.getItem("followUpFeatureDismissed") === "true";
  });
  const [leadServices, setLeadServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      console.log("Lead ID from params:", leadId);

      try {
        const [fetchedLead, fetchedLocations, fetchedServices] = await Promise.all([
          getLead(authToken, leadId),
          getLocations(authToken),
          getLeadServices(authToken, leadId),
        ]);
        console.log("Fetched lead location:", fetchedLead.location);
        console.log("Fetched locations:", fetchedLocations);
        console.log("Locations IDs:", fetchedLocations.map(loc => loc.id));
        console.log("Set formData.location:", fetchedLead.location && fetchedLead.location.id != null ? String(fetchedLead.location.id) : "");
        setFormData({
          ...fetchedLead,
          location: fetchedLead.location && fetchedLead.location.id != null ? String(fetchedLead.location.id) : "",
          lead_category: fetchedLead.lead_category || "prospect",
          source: fetchedLead.source || "",
          follow_up_date: fetchedLead.follow_up_date || null,
          address: fetchedLead.address || {
            street: "",
            city: "",
            state: "",
            country: "",
            postal_code: "",
          },
        });
        setLocations(fetchedLocations);
        setLeadServices(fetchedServices || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to fetch lead data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [authToken, leadId]);

  // Mark as interacted when the user opens the dropdown
  useEffect(() => {
    if (isOpen && !hasInteracted) {
      setHasInteracted(true);
      localStorage.setItem("hasInteractedWithFollowUpDate", "true");
    }
  }, [isOpen, hasInteracted]);

  const handleDismissFollowUp = () => {
    setFollowUpDismissed(true);
    localStorage.setItem("followUpFeatureDismissed", "true");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      follow_up_date: date ? format(date, 'yyyy-MM-dd') : null,
    }));
  };

  const handleServicesChange = async (updatedServices) => {
    setServicesLoading(true);
    try {
      await updateLeadServices(authToken, leadId, updatedServices);
      setLeadServices(updatedServices);
      // Refetch lead data to get updated estimated_value
      const updatedLead = await getLead(authToken, leadId);
      setFormData(prev => ({ ...prev, estimated_value: updatedLead.estimated_value }));
    } catch (error) {
      console.error("Error updating services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.location === "") {
      setError("Please select a location");
      return;
    }
    const needsLossReason = formData.status === "lost" || formData.lead_category === "not_applicable";
    if (needsLossReason && !formData.loss_reason) {
      setError("Please select a loss reason when marking a lead as lost or not applicable");
      return;
    }
    try {
      const submitData = {
        ...formData,
        location: formData.location ? parseInt(formData.location, 10) : null,
        source: formData.source || null,
        follow_up_date: formData.follow_up_date || null,
        loss_reason: needsLossReason ? formData.loss_reason : null,
        loss_reason_notes: needsLossReason ? formData.loss_reason_notes : null,
      };
      console.log("Submitting form data:", submitData);
      await editLead(authToken, leadId, submitData);
      navigate("/leads");
    } catch (error) {
      console.error("Error updating lead:", error);
      setError("Failed to update lead. Please try again.");
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 bg-background">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-3xl font-bold text-gray-800 flex items-center space-x-2 mb-4 sm:mb-0">
          {(formData.first_name?.trim().length === 0 ||
            formData.last_name?.trim().length === 0) &&
          formData.type === "phonecall"
            ? formatPhoneNumber(formData.phone)
            : `${formData.first_name} ${formData.last_name}`}
        </h3>
        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6"
            role="alert"
          >
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center px-4 py-2 border border-text-primary text-primary rounded-lg hover:bg-text-primary hover:text-white focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Leads
        </button>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-sm rounded-lg p-8 mb-4 hover:border hover:border-teal-200 transition duration-300"
      >
        {/* Contact Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="first_name"
            >
              First Name
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="first_name"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="last_name"
            >
              Last Name
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="last_name"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="email"
            >
              Email
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="phone"
            >
              Phone
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="phone"
              type="tel"
              name="phone"
              value={formatPhoneNumber(formData.phone)}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Follow-Up Planning Section - PROMINENT PLACEMENT */}
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <svg
              className="h-6 w-6 text-orange-600 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-orange-800">
              Schedule Follow-Up
            </h3>
            {!followUpDismissed && !hasInteracted && (
              <span className="ml-3 inline-flex items-center px-3 py-1 text-sm font-semibold text-white bg-orange-500 rounded-full hover:bg-orange-600 transition duration-300 cursor-pointer relative group z-50">
                💡 Set Reminder
                <button
                  onClick={handleDismissFollowUp}
                  className="ml-2 text-white hover:text-orange-200 focus:outline-none"
                  aria-label="Dismiss reminder feature notification"
                >
                  ✕
                </button>
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className="block text-orange-700 text-sm font-medium mb-2"
                htmlFor="follow_up_date"
              >
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Reminder
                </div>
              </label>
              <DatePicker
                className="border-2 border-orange-300 rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition duration-300 bg-white"
                id="follow_up_date"
                selected={
                  formData.follow_up_date
                    ? parse(formData.follow_up_date, "yyyy-MM-dd", new Date())
                    : null
                }
                onCalendarOpen={() => {
                  setIsOpen(!isOpen);
                }}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                placeholderText="Click to set a reminder"
                isClearable
              />
            </div>
            <div className="flex items-end">
              <div className="bg-orange-100 p-4 rounded-lg w-full">
                <p className="text-sm text-orange-700 font-medium mb-1">
                  💡 Pro Tip:
                </p>
                <p className="text-xs text-orange-600">
                  Set a follow-up date to track when you need to contact this
                  lead again.
                  {formData.follow_up_date
                    ? " You'll see this in your due leads filter!"
                    : " This helps you stay organized!"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <svg
              className="h-6 w-6 text-green-600 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="text-lg font-semibold text-green-800">
              Services of Interest
            </h3>
            {servicesLoading && (
              <div className="ml-3 animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            )}
          </div>
          <ServiceSelector
            selectedServices={leadServices}
            onServicesChange={handleServicesChange}
            authToken={authToken}
            leadId={leadId}
            lead={formData}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="location"
            >
              Location
            </label>
            <select
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="status"
            >
              Status
            </label>
            <select
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {LEAD_STATUSES.map((status) => (
                <option key={status.key} value={status.key}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="lead_category"
            >
              Lead Category
            </label>
            <select
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
              id="lead_category"
              name="lead_category"
              value={formData.lead_category}
              onChange={handleChange}
            >
              {LEAD_CATEGORIES.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="source"
            >
              Source
            </label>
            <select
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 hover:border-text-primary transition duration-300"
              id="source"
              name="source"
              value={formData.source}
              onChange={handleChange}
            >
              <option value="">Select a Lead Source</option>
              {LEAD_SOURCES.map((source) => (
                <option key={source.key} value={source.key}>
                  {source.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Loss Reason Section - Only show when status is lost or category is not_applicable */}
        {(formData.status === "lost" || formData.lead_category === "not_applicable") && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <svg
                className="h-6 w-6 text-red-600 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-red-800">
                Loss Reason Required
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-red-700 text-sm font-medium mb-2"
                  htmlFor="loss_reason"
                >
                  Reason for Loss *
                </label>
                <select
                  className="border-2 border-red-300 rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-300 bg-white"
                  id="loss_reason"
                  name="loss_reason"
                  value={formData.loss_reason}
                  onChange={handleChange}
                  required={formData.status === "lost" || formData.lead_category === "not_applicable"}
                >
                  <option value="">Select a reason</option>
                  {(formData.lead_category === "not_applicable" ? NOT_APPLICABLE_REASONS : LEAD_LOSS_REASONS).map((reason) => (
                    <option key={reason.key} value={reason.key}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className="block text-red-700 text-sm font-medium mb-2"
                  htmlFor="loss_reason_notes"
                >
                  Additional Notes
                </label>
                <textarea
                  className="border-2 border-red-300 rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-300 bg-white resize-none"
                  id="loss_reason_notes"
                  name="loss_reason_notes"
                  value={formData.loss_reason_notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Provide additional context for the loss reason..."
                />
              </div>
            </div>
          </div>
        )}

        {/* City and State */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="address.street"
            >
              Street
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="address.street"
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="address.city"
            >
              City
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="address.city"
              type="text"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="address.state"
            >
              State
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="address.state"
              type="text"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="address.country"
            >
              Country
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="address.country"
              type="text"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
            />
          </div>
          <div>
            <label
              className="block text-gray-600 text-sm font-medium mb-2"
              htmlFor="address.postal_code"
            >
              Postal Code
            </label>
            <input
              className="border border-border rounded-lg w-full py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
              id="address.postal_code"
              type="text"
              name="address.postal_code"
              value={formData.address.postal_code}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            className="bg-secondary hover:bg-secondary/90/85 text-white font-medium py-2 px-6 rounded-lg focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300"
            type="submit"
          >
            Update Lead
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadEdit;